import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  InteractionManager,
  type LayoutChangeEvent,
  Linking,
  Platform,
  Pressable,
  type ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import BackMobileIcon from '@/assets/images/level-detail/back-mobile.svg';
import { HorizontalScrollView, Stack } from '@/components/layout';
import { showAlert, SvgIcon, Text } from '@/components/ui';
import { FLOATING_TAB_BAR_CLEARANCE_TOKEN } from '@/components/ui/tabbar';
import { STRINGS } from '@/constants/strings';
import { NotificationPermissionBanner } from '@/features/guardian-profile/components/notification-preferences/notification-permission-banner';
import {
  NotificationChip,
  NotificationEmptyState,
  NotificationItem,
  NotificationSettingsMenu,
} from '@/features/guardian/components/notifications';
import { createStyles, useBottomInsetSmart, useNotificationPermission, useScaling } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { colors, guardianColors, withOpacity } from '@/theme/colors';
import { Device } from '@/theme/scaling';

import { useFamilyEvents } from '@/features/guardian/hooks/use-family-events';
import { useGuardianNotifications } from '@/features/guardian/hooks/use-guardian-notifications';
import { useOpenEventDetail } from '@/features/guardian/hooks/use-open-event-detail';
import { NOTIFICATION_FILTERS } from '@/features/guardian/notifications/constants';
import type {
  GuardianNotification,
  GuardianNotificationFilter,
} from '@/features/guardian/notifications/types';
import { resolveFamilyEvent } from '@/features/guardian/utils/resolve-family-event';

type ScreenNavigation = GuardianStackScreenProps<'GuardianNotifications'>['navigation'];
const strings = STRINGS.guardian.notifications;
const LONG_LIST_ITEM_THRESHOLD = 4;

export function GuardianNotificationsScreen() {
  const navigation = useNavigation<ScreenNavigation>();
  const { scale, isTablet, deviceHeight } = useScaling();
  const insets = useSafeAreaInsets();
  const bottomInset = useBottomInsetSmart();
  const styles = useStyles();
  const {
    isDenied,
    isUndetermined,
    canAsk: canAskNotificationPermission,
    isSupported: isNotificationSupported,
    refresh: refreshNotificationPermission,
    request: requestNotificationPermission,
  } = useNotificationPermission();
  const filterScrollRef = useRef<ScrollView>(null);
  const filterLayoutsRef = useRef<Partial<Record<GuardianNotificationFilter, number>>>({});
  const requestedPermissionRef = useRef(false);
  const { events } = useFamilyEvents();
  const openEventDetail = useOpenEventDetail();
  const handleEventNotificationPress = useCallback(
    (item: GuardianNotification) => {
      const isEventDestination =
        item.destination === 'booking_details' ||
        Boolean(item.eventId) ||
        item.type === 'tournament-updates';
      if (!isEventDestination) return false;

      const type =
        item.type === 'tournament-updates'
          ? 'tournament'
          : item.apiType.startsWith('class_')
            ? 'practice'
            : undefined;
      const event = resolveFamilyEvent(events, {
        eventId: item.eventId,
        bookingId: item.bookingId,
        playerId: item.playerId,
        type,
      });
      if (!event) return false;

      openEventDetail(event);
      return true;
    },
    [events, openEventDetail],
  );
  const {
    filter,
    setFilter,
    notifications,
    totalNotificationCount,
    unreadCount,
    isReady,
    isRefreshing,
    isFetchingNextPage,
    handleNotificationPress,
    handleMarkAllRead,
    handleDelete,
    handleRefresh,
    handleLoadMore,
  } = useGuardianNotifications({ onEventNotificationPress: handleEventNotificationPress });

  const handleItemPress = useCallback(
    (item: GuardianNotification) => {
      if (isTablet && navigation.canGoBack() && !!item.destination) navigation.goBack();
      handleNotificationPress(item);
    },
    [handleNotificationPress, isTablet, navigation],
  );
  const [showSettings, setShowSettings] = useState(false);
  const isWeb = Platform.OS === 'web';
  const showPermissionBanner = isWeb
    ? isNotificationSupported && (isUndetermined || isDenied)
    : isDenied;

  useEffect(() => {
    if (isWeb) return undefined;

    let cancelled = false;
    const task = InteractionManager.runAfterInteractions(() => {
      if (requestedPermissionRef.current) return;
      requestedPermissionRef.current = true;

      refreshNotificationPermission().then(status => {
        if (!cancelled && status === 'undetermined') requestNotificationPermission();
      });
    });

    return () => {
      cancelled = true;
      task.cancel();
    };
  }, [isWeb, refreshNotificationPermission, requestNotificationPermission]);

  const title = unreadCount > 0 ? strings.titleWithUnreadCount(unreadCount) : strings.title;
  const tabletPopupTop = insets.top + scale(55);
  const availableWindowHeight = deviceHeight + insets.top + insets.bottom;
  const tabletPopupHeight = useMemo(() => {
    const minimumHeight = scale(400);
    const bottomClearance =
      scale(FLOATING_TAB_BAR_CLEARANCE_TOKEN + 8) +
      bottomInset +
      (Platform.OS === 'android' ? Device.bottomHeight : 0);
    const maximumHeight = availableWindowHeight - tabletPopupTop - bottomClearance;
    const permissionHeight = showPermissionBanner ? scale(64) : 0;

    if (isReady && notifications.length === 0) {
      return Math.min(maximumHeight, minimumHeight + permissionHeight);
    }

    if (totalNotificationCount >= LONG_LIST_ITEM_THRESHOLD && notifications.length > 0) {
      return maximumHeight;
    }

    const itemHeight = scale(filter === 'all' ? 100 : 72);
    const itemGaps = scale(Math.max(notifications.length - 1, 0) * 12);
    const contentHeight =
      scale(60 + 48 + 28) + permissionHeight + notifications.length * itemHeight + itemGaps;

    return Math.min(maximumHeight, Math.max(minimumHeight, contentHeight));
  }, [
    availableWindowHeight,
    filter,
    bottomInset,
    isReady,
    notifications.length,
    showPermissionBanner,
    scale,
    tabletPopupTop,
    totalNotificationCount,
  ]);

  const openPreferences = useCallback(() => {
    setShowSettings(false);
    navigation.navigate('GuardianNotificationPreferences', { source: 'notification-center' });
  }, [navigation]);

  const handlePermissionPress = useCallback(() => {
    if (!isWeb) {
      Linking.openSettings();
      return;
    }

    if (canAskNotificationPermission) {
      requestNotificationPermission();
      return;
    }

    showAlert({
      title: STRINGS.common.noticeTitle,
      message: STRINGS.common.webNotificationsDeniedMessage,
      buttons: [{ text: STRINGS.common.buttons.dismiss }],
    });
  }, [canAskNotificationPermission, isWeb, requestNotificationPermission]);

  const handleBackPress = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    navigation.navigate('GuardianDashboard');
  }, [navigation]);

  const handleFilterPress = useCallback(
    (value: GuardianNotificationFilter) => {
      setFilter(value);
      const x = filterLayoutsRef.current[value];
      if (x === undefined) return;
      requestAnimationFrame(() => {
        filterScrollRef.current?.scrollTo({ x: Math.max(0, x - scale(16)), animated: true });
      });
    },
    [scale, setFilter],
  );

  const handleFilterLayout = useCallback(
    (value: GuardianNotificationFilter, event: LayoutChangeEvent) => {
      filterLayoutsRef.current[value] = event.nativeEvent.layout.x;
    },
    [],
  );

  const handleMarkAllReadPress = useCallback(() => {
    handleMarkAllRead();
    setShowSettings(false);
  }, [handleMarkAllRead]);

  const content = (
    <LinearGradient
      colors={isTablet ? [colors['0'], colors['0']] : ['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      style={[
        styles.screen,
        isTablet ? styles.tabletPanel : null,
        isTablet ? { top: tabletPopupTop, height: tabletPopupHeight } : null,
      ]}>
      <Stack
        direction="row"
        align="center"
        justify="space-between"
        style={styles.headerRow}
        paddingTop={isTablet ? scale(16) : insets.top + scale(4)}
        paddingX={scale(isTablet ? 18 : 20)}
        paddingBottom={scale(isTablet ? 4 : 8)}>
        {!isTablet ? (
          <Pressable accessibilityRole="button" onPress={handleBackPress} style={styles.backButton}>
            <BackMobileIcon width={scale(24)} height={scale(24)} />
          </Pressable>
        ) : null}
        <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.headerTitle}>
          {title}
        </Text>
        <View style={styles.settingsAnchor}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={strings.accessibility.settings}
            onPress={() => setShowSettings(value => !value)}
            style={styles.headerButton}>
            <SvgIcon name="IcNotificationSettings" size={scale(24)} />
          </Pressable>
          {showSettings ? (
            <NotificationSettingsMenu
              onMarkAllRead={handleMarkAllReadPress}
              onPreferences={openPreferences}
            />
          ) : null}
        </View>
      </Stack>

      <HorizontalScrollView
        ref={filterScrollRef}
        horizontal
        style={styles.filtersScroll}
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filters}>
        {NOTIFICATION_FILTERS.map(item => (
          <NotificationChip
            key={item.value}
            active={filter === item.value}
            label={item.label}
            onLayout={event => handleFilterLayout(item.value, event)}
            onPress={() => handleFilterPress(item.value)}
          />
        ))}
      </HorizontalScrollView>

      {showPermissionBanner ? (
        <NotificationPermissionBanner
          message={strings.permissionWarning}
          onPress={handlePermissionPress}
          style={styles.permissionBanner}
        />
      ) : null}

      <FlatList
        data={notifications}
        style={styles.list}
        keyExtractor={item => item.id}
        renderItem={({ item }) => (
          <NotificationItem
            item={item}
            showType={filter === 'all'}
            onDelete={handleDelete}
            onPress={handleItemPress}
          />
        )}
        contentContainerStyle={[
          styles.listContent,
          notifications.length === 0 ? styles.emptyListContent : null,
        ]}
        ListEmptyComponent={
          isReady ? <NotificationEmptyState onSettings={openPreferences} /> : null
        }
        ListFooterComponent={
          isFetchingNextPage ? <ActivityIndicator style={styles.loadMoreIndicator} /> : null
        }
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.4}
        onRefresh={handleRefresh}
        removeClippedSubviews={Platform.OS !== 'ios'}
        refreshing={isRefreshing}
        showsVerticalScrollIndicator={false}
      />
    </LinearGradient>
  );

  return (
    <GestureHandlerRootView style={styles.gestureRoot}>
      {isTablet ? (
        <View style={styles.tabletOverlay}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={strings.accessibility.close}
            onPress={navigation.goBack}
            style={styles.backdrop}
          />
          {content}
        </View>
      ) : (
        content
      )}
    </GestureHandlerRootView>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  gestureRoot: { flex: 1 },
  screen: { flex: 1 },
  tabletOverlay: { flex: 1, backgroundColor: 'transparent' },
  backdrop: { ...StyleSheet.absoluteFillObject },
  tabletPanel: {
    position: 'absolute',
    right: scale(20),
    width: scale(416),
    minHeight: scale(400),
    borderRadius: scale(12),
    overflow: 'hidden',
    shadowColor: colors['10'],
    shadowOffset: { width: 0, height: scale(10) },
    shadowOpacity: 0.1,
    shadowRadius: scale(10),
    elevation: 8,
  },
  headerButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withOpacity(colors['0'], 0.5),
  },
  headerRow: { zIndex: 30 },
  settingsAnchor: { position: 'relative', zIndex: 30 },
  backButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors['0'],
    shadowColor: guardianColors['10'],
    shadowOpacity: 0.06,
    shadowOffset: { width: 0, height: scale(2) },
    shadowRadius: scale(6),
    elevation: 1,
  },
  headerTitle: {
    color: colors['10'],
    textAlign: isTablet ? 'left' : 'center',
    flex: isTablet ? 1 : undefined,
  },
  filtersScroll: { flexGrow: 0, flexShrink: 0 },
  filters: {
    gap: scale(8),
    paddingHorizontal: scale({ phone: 16, tablet: 18 }),
    paddingVertical: scale(8),
  },
  permissionBanner: {
    marginHorizontal: scale({ phone: 16, tablet: 18 }),
    marginTop: scale(4),
    marginBottom: scale(8),
  },
  list: { flex: 1 },
  listContent: {
    gap: scale(12),
    paddingHorizontal: scale({ phone: 16, tablet: 18 }),
    paddingBottom: scale({ phone: 28, tablet: 40 }),
  },
  emptyListContent: { flexGrow: 1 },
  loadMoreIndicator: { marginVertical: scale(16) },
}));
